import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LoadingSkeleton } from '@/components/ui/loading-spinner';
import { Edit, Settings, Trash2, Users, Plus, QrCode, ExternalLink, BarChart3 } from 'lucide-react';
import { JobPosting } from './types';

interface JobListProps {
  jobPostings: JobPosting[];
  applications: {[key: string]: number};
  onCreateJob: () => void;
  onEditJob: (job: JobPosting) => void;
  onDeleteJob: (job: JobPosting) => void;
  onViewApplications: (job: JobPosting) => void;
  onToggleStatus: (job: JobPosting) => void;
  onEditForm: (job: JobPosting) => void;
  onSelectJobForAnalytics?: (jobId: string) => void;
  selectedJobForAnalytics?: string;
  loading?: boolean;
}

const JobList: React.FC<JobListProps> = ({
  jobPostings,
  applications,
  onCreateJob,
  onEditJob,
  onDeleteJob,
  onViewApplications,
  onToggleStatus,
  onEditForm,
  onSelectJobForAnalytics,
  selectedJobForAnalytics,
  loading = false
}) => {
  const isExpired = (expiresAt: string | null) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const getStatusBadge = (job: JobPosting) => {
    if (!job.is_active) return <Badge>Inactive</Badge>;
    if (isExpired(job.expires_at)) return <Badge variant="destructive">Expired</Badge>;
    return <Badge variant="default">Active</Badge>;
  };

  if (loading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
          <div className="flex justify-between items-center">
            <CardTitle className="text-sm font-medium">Job Postings</CardTitle>
            <Button onClick={onCreateJob}>
              <Plus className="h-4 w-4 mr-2" />
              Create Job Posting
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <LoadingSkeleton variant="table" rows={3} />
        </CardContent>
      </Card>
    );
  }

  if (jobPostings.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Users className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Job Postings Yet</h3>
          <p className="text-muted-foreground text-center mb-4">
            Start recruiting by creating your first job posting. You can generate QR codes to share job applications.
          </p>
          <Button onClick={onCreateJob}>
            <Plus className="h-4 w-4 mr-2" />
            Create Your First Job Posting
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <CardTitle className="text-sm font-medium">Job Postings</CardTitle>
            {jobPostings.length > 0 && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground bg-blue-50 dark:bg-blue-950 px-2 py-1 rounded-md">
                <BarChart3 className="h-3 w-3" />
                <span>Analytics Available</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-4">
            {onSelectJobForAnalytics && jobPostings.length > 0 && (
              <div className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Analytics for:</span>
                <Select value={selectedJobForAnalytics} onValueChange={onSelectJobForAnalytics}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Job Postings</SelectItem>
                    {jobPostings.map(job => (
                      <SelectItem key={job.id} value={job.id}>{job.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button onClick={onCreateJob}>
              <Plus className="h-4 w-4 mr-2" />
              Create Job Posting
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Experience</TableHead>
              <TableHead>Applications</TableHead>
              <TableHead>QR Code</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {jobPostings.map((job) => (
              <TableRow key={job.id}>
                <TableCell>
                  <div>
                    <div className="font-medium">{job.title}</div>
                    {job.location && (
                      <div className="text-sm text-muted-foreground">{job.location}</div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline">
                    {job.employment_type.replace('-', ' ')}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge>
                    {job.experience_level.replace('-', ' ')}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onViewApplications(job)}
                    className="flex items-center gap-1"
                  >
                    <Users className="h-4 w-4" />
                    {applications[job.id] || 0} applications
                  </Button>
                </TableCell>
                <TableCell>
                  {job.qr_codes && job.qr_codes.length > 0 ? (
                    <div className="flex items-center space-x-2">
                      <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200 dark:bg-green-900 dark:text-green-200 dark:border-green-700 flex items-center gap-1">
                        <QrCode className="h-3 w-3" />
                        Active
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(job.qr_codes![0].url, '_blank')}
                        className="h-6 w-6 p-0"
                      >
                        <ExternalLink className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <Badge variant="outline" className="bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700">
                      No QR Code
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  {getStatusBadge(job)}
                </TableCell>
                <TableCell>
                  {new Date(job.created_at).toLocaleDateString('en-GB').replace(/\//g, '.')}
                </TableCell>
                <TableCell>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEditJob(job)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEditForm(job)}
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onToggleStatus(job)}
                    >
                      {job.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => onDeleteJob(job)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default JobList;